import React from 'react';
import useRole from '../Provider/TableContext/RoleContext';
import useResponsibility from '../Provider/TableContext/ResponsibilityContext';
import useABC from '../Provider/TableContext/ABCContext';
import { AllProcessSettled } from '../Functions/Helper/Async';
import { formDataToProfession, professionToFormData } from '../Functions/Helper/Model';
import {  trackException } from '../Hooks/withApplicationInsight';

export function useProfessionSubmission(roleId, setRoleId) {
    const { roles, processRoleChanges, loading: roleLoading } = useRole();
    const { responsibilities, processResponsibilityChanges, loading: responsibilitiesLoading } = useResponsibility();
    const { abcs, processABCChanges, loading: abcLoading } = useABC();

    const loading = React.useMemo(() => roleLoading || responsibilitiesLoading || abcLoading, 
        [roleLoading, responsibilitiesLoading, abcLoading]);

    const value = React.useMemo(() => {
        if(roleId && !loading && Array.isArray(roles)) {
            const role = roles.find(role => role.mcb_roleid === roleId);
            if (role) return professionToFormData(role, responsibilities, abcs);
        }
        return {};

    }, [roleId, loading, roles, responsibilities, abcs]);
    const [roleFormData, setRoleFormData] = React.useState(value);

    React.useEffect(() => { 
        setRoleFormData(value);
    }, [value]);
    
    const onProfessionSubmit = React.useCallback(async (preProcessData) => {
        let context = {};
        
        try {
            context = {formData: roleFormData};
            const profession = typeof preProcessData === "function"?  
                preProcessData(roleFormData || {}) : {...(roleFormData || {})};
            
            const responsibilityChanges = [], abcChanges = [];
            const roleChange = formDataToProfession(profession, responsibilityChanges, abcChanges)
            if (roleChange.mcb_roleid) {
                responsibilities.forEach(({_mcb_role_value, mcb_responsibilityid}) => {
                    if (_mcb_role_value === roleChange.mcb_roleid) {
                        if (!responsibilityChanges.some(respC => respC.mcb_responsibilityid === mcb_responsibilityid)) {
                            return responsibilityChanges.push({ mcb_responsibilityid, deleted: true });
                        }
                    }
                    return false;
                });

                abcs.forEach(({mcb_abcid, _mcb_role_value}) => {
                    if (_mcb_role_value === roleChange.mcb_roleid) {
                        if (!abcChanges.some(abC => abC.mcb_abcid === mcb_abcid)) {
                            return abcChanges.push({ mcb_abcid, deleted: true});
                        }
                    }
                    return false;
                });
            }
            const result = await AllProcessSettled(
                processRoleChanges([roleChange]),
                processResponsibilityChanges(responsibilityChanges),
                processABCChanges(abcChanges)
            ).then(results => results[0]).then(result => result.value[0]);
            setRoleId(result.mcb_roleid);
        } catch(error) {
            trackException({error, properties: {
                context,
                relatedTo: "Profession Submission"
            }});
            console.error(error);
            throw error;
        }
    }, [processRoleChanges, processResponsibilityChanges, processABCChanges, responsibilities, abcs, roleFormData, setRoleId]);

    const disableRoleSubmit = React.useMemo(() => {
        return !(
            roleFormData?.mcb_roletitle?.length > 0
            && roleFormData?.mcb_rolecompanyname?.length > 0
            && roleFormData?.mcb_starteddate
        );
    }, [roleFormData]);

    return {onProfessionSubmit, disableRoleSubmit, roleFormData, setRoleFormData, loading};
}
